import { useGetList } from "ra-core";

import { SectionCard } from "../data-display/SectionCard";
import type { LeadForm } from "./formModel";

interface ViewCount {
  id: string;
  form_id: string;
  day: string;
  views: number;
}

interface Submission {
  id: string;
  created_at: string;
}

const DAYS = 30;

/**
 * Form analytics: views (from the daily counter the public page bumps),
 * submissions, and the conversion rate between them, over the last 30
 * days. Honest denominators: no views recorded means no rate, not 0%.
 */
export const FormAnalyticsPanel = ({ form }: { form: LeadForm }) => {
  const since = new Date(Date.now() - DAYS * 24 * 3600 * 1000)
    .toISOString()
    .slice(0, 10);

  const { data: viewRows } = useGetList<ViewCount>("form_view_counts", {
    pagination: { page: 1, perPage: DAYS + 1 },
    sort: { field: "day", order: "DESC" },
    filter: { form_id: form.id, "day@gte": since },
  });
  const { data: submissions } = useGetList<Submission>("form_submissions", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "created_at", order: "DESC" },
    filter: { form_id: form.id, "created_at@gte": since },
  });

  const views = (viewRows ?? []).reduce((sum, row) => sum + row.views, 0);
  const subs = (submissions ?? []).length;
  const rate = views > 0 ? Math.round((subs / views) * 100) : null;

  return (
    <SectionCard title={`Analytics, last ${DAYS} days`}>
      <div className="flex flex-wrap gap-6 px-1 py-1">
        <div className="flex flex-col">
          <span className="text-xl font-semibold">{views}</span>
          <span className="text-[11.5px] text-muted-foreground">Views</span>
        </div>
        <div className="flex flex-col">
          <span className="text-xl font-semibold">{subs}</span>
          <span className="text-[11.5px] text-muted-foreground">
            Submissions
          </span>
        </div>
        <div className="flex flex-col">
          <span className="text-xl font-semibold">
            {rate === null ? "n/a" : `${rate}%`}
          </span>
          <span className="text-[11.5px] text-muted-foreground">
            Conversion
          </span>
        </div>
      </div>
      {rate === null ? (
        <p className="px-1 text-[11.5px] text-muted-foreground">
          Conversion appears once the public page records views.
        </p>
      ) : null}
    </SectionCard>
  );
};
